import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'

import { useNavItems } from '@/lib/core/components/Layout/SidebarNav'
import { useCommandPaletteStore } from '@/lib/stores/commandPaletteStore'

import { CommandPaletteItem } from './CommandPalette.types'
import {
  filterNavItems,
  getNextIndex,
  getPreviousIndex,
  isPaletteShortcut,
} from './CommandPalette.utils'

export const useCommandPalette = () => {
  const { isOpen, close, toggle } = useCommandPaletteStore()
  const { navigableItems, canNavigateTo } = useNavItems()
  const router = useRouter()

  const [query, setQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const results = useMemo(() => filterNavItems(navigableItems, query), [navigableItems, query])

  // A narrowed list must not leave the highlight pointing at a row that moved.
  useEffect(() => {
    setHighlightedIndex(0)
  }, [query])

  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (!isPaletteShortcut(event)) {
        return
      }

      event.preventDefault()
      toggle()
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [toggle])

  useEffect(() => {
    if (!isOpen) {
      setQuery('')
      setHighlightedIndex(0)

      return
    }

    inputRef.current?.focus()
  }, [isOpen])

  const navigateTo = useCallback(
    (item: CommandPaletteItem) => {
      // Defence in depth: the list is already filtered by `useNavItems`, but a
      // future refactor of the rendering must not be able to reopen the hole.
      if (!canNavigateTo(item)) {
        return
      }

      close()
      router.push(item.href)
    },
    [canNavigateTo, close, router],
  )

  const handleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setHighlightedIndex((current) => getNextIndex(current, results.length))

        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setHighlightedIndex((current) => getPreviousIndex(current, results.length))

        return
      }

      if (event.key === 'Enter') {
        event.preventDefault()
        const item = results[highlightedIndex]

        if (item) {
          navigateTo(item)
        }
      }
    },
    [highlightedIndex, navigateTo, results],
  )

  return {
    close,
    handleInputKeyDown,
    highlightedIndex,
    inputRef,
    isOpen,
    navigateTo,
    query,
    results,
    setHighlightedIndex,
    setQuery,
  }
}
